/**
 * WhatIfAnalysisPanel - Sprint 23 P2
 * 
 * Allows users to simulate converting a demand into different project structures
 * and preview capacity, budget, and variance impact before committing.
 * 
 * Features:
 * - Multiple structure options (Single Project, Multi-Epic, Program Breakdown)
 * - Side-by-side comparison
 * - Impact metrics visualization
 * - Promote to Scenario capability
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sparkles, TrendingUp, TrendingDown, DollarSign, Users, Calendar,
  AlertTriangle, CheckCircle, Target, ChevronRight, X, RefreshCw,
  Layers, FileSpreadsheet, GitBranch, Award, ArrowRight, Download
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

// Structure type configs
const STRUCTURE_CONFIGS = {
  single_project: {
    label: 'Single Project',
    description: 'One project with all work items',
    icon: FileSpreadsheet,
    color: 'blue',
    recommended: 'Under $200K'
  },
  multi_epic: {
    label: 'Multiple Epics',
    description: 'One project with multiple epics',
    icon: Layers,
    color: 'purple',
    recommended: '$200K - $500K'
  },
  program_breakdown: {
    label: 'Program Breakdown',
    description: 'Multiple sub-projects in a program',
    icon: GitBranch,
    color: 'amber',
    recommended: 'Over $500K'
  }
};

// Metric card component
const MetricCard = ({ label, value, icon: Icon, color, trend, subtitle }) => {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;
  
  return (
    <div className={`p-3 rounded-lg border ${color || 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-1">
        {Icon && <Icon className="w-4 h-4 text-gray-500" />}
        {TrendIcon && <TrendIcon className={`w-3 h-3 ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`} />}
      </div>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// Comparison result card
const SimulationResultCard = ({ result, isRecommended, onSelect, selected }) => {
  const config = STRUCTURE_CONFIGS[result.structure_type] || STRUCTURE_CONFIGS.single_project;
  const Icon = config.icon;
  
  const riskColor = result.metrics.risk_score < 30 ? 'text-green-600' : 
                    result.metrics.risk_score < 70 ? 'text-amber-600' : 'text-red-600';
  
  return (
    <div 
      className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${
        selected ? 'ring-2 ring-blue-500 border-blue-500' : 'hover:border-gray-400'
      } ${isRecommended ? 'border-green-500' : ''}`}
      onClick={() => onSelect(result.structure_id)}
      data-testid={`simulation-result-${result.structure_id}`}
    >
      {/* Header */}
      <div className={`p-4 ${isRecommended ? 'bg-green-50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${config.color}-600`} />
            <span className="font-medium">{result.structure_name}</span>
          </div>
          {isRecommended && (
            <Badge className="bg-green-100 text-green-800">
              <Award className="w-3 h-3 mr-1" />
              Recommended
            </Badge>
          )}
        </div>
        <p className="text-sm text-gray-500">{config.description}</p>
      </div>
      
      {/* Metrics Grid */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <MetricCard
          label="Budget Required"
          value={`$${(result.metrics.budget_required / 1000).toFixed(0)}K`}
          icon={DollarSign}
          color={result.metrics.budget_variance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}
        />
        <MetricCard
          label="Timeline"
          value={`${result.metrics.timeline_weeks} weeks`}
          icon={Calendar}
        />
        <MetricCard
          label="Capacity Need"
          value={`${result.metrics.capacity_required.toFixed(0)} FTE-wks`}
          icon={Users}
          color={result.metrics.capacity_gap > 0 ? 'bg-amber-50 border-amber-200' : ''}
        />
        <MetricCard
          label="Risk Score"
          value={`${result.metrics.risk_score.toFixed(0)}/100`}
          icon={AlertTriangle}
          color={result.metrics.risk_score > 70 ? 'bg-red-50 border-red-200' : result.metrics.risk_score > 30 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}
        />
      </div>
      
      {/* ROI */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
          <span className="text-sm text-blue-700">Estimated ROI</span>
          <span className="font-bold text-blue-800">{result.metrics.roi_estimate.toFixed(1)}%</span>
        </div>
      </div>
      
      {/* Warnings */}
      {result.warnings?.length > 0 && (
        <div className="px-4 pb-3">
          <div className="text-xs space-y-1">
            {result.warnings.slice(0, 2).map((warning, idx) => (
              <div key={idx} className="flex items-start gap-1 text-amber-600">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{warning}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Select indicator */}
      <div className={`p-2 text-center text-sm ${selected ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-500'}`}>
        {selected ? 'Selected for promotion' : 'Click to select'}
      </div>
    </div>
  );
};

// Main component
const WhatIfAnalysisPanel = ({ demandId, demandTitle, demandBudget, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Simulation inputs
  const [budgetCap, setBudgetCap] = useState(demandBudget || 100000);
  const [structures, setStructures] = useState([
    { type: 'single_project', name: 'Single Project Approach', team_size: 5, estimated_duration_weeks: 12 },
    { type: 'multi_epic', name: 'Multi-Epic Structure', team_size: 8, estimated_duration_weeks: 16, epics: [{}, {}, {}] },
    { type: 'program_breakdown', name: 'Program with Sub-Projects', team_size: 15, estimated_duration_weeks: 24, projects: [{}, {}] }
  ]);

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const response = await api.post('/whatif/simulate', {
        demand_id: demandId,
        structures: structures,
        budget_cap: budgetCap
      });
      
      setResults(response.data);
      setSelectedStructure(response.data.best_option);
      toast.success('Simulation complete');
    } catch (error) {
      console.error('Simulation failed:', error);
      toast.error('Failed to run simulation');
    } finally {
      setSimulating(false);
    }
  };

  const handlePromote = async () => {
    if (!selectedStructure || !results) return;
    
    setPromoting(true);
    try {
      const response = await api.post('/whatif/promote', {
        simulation_id: results.simulation_id,
        structure_id: selectedStructure,
        scenario_name: scenarioName || undefined
      });
      
      toast.success('Promoted to scenario successfully!');
      setShowPromoteDialog(false);
      
      // Navigate to scenario planning
      navigate('/scenarios', { 
        state: { newScenarioId: response.data.scenario_id }
      });
    } catch (error) {
      console.error('Promotion failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to promote to scenario');
    } finally {
      setPromoting(false);
    }
  };

  // Sprint 24: Export PDF handler
  const handleExportPDF = async () => {
    if (!results?.simulation_id) {
      toast.error('No simulation to export');
      return;
    }
    
    setExporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatif/export/${results.simulation_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `whatif_comparison_${results.simulation_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            What-If Analysis
          </h3>
          <p className="text-sm text-gray-500">
            Simulate different project structures for: {demandTitle}
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Simulation Parameters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="budgetCap">Budget Cap ($)</Label>
              <Input
                id="budgetCap"
                type="number"
                value={budgetCap}
                onChange={(e) => setBudgetCap(Number(e.target.value))}
                data-testid="budget-cap-input"
              />
            </div>
            <div>
              <Label>Structures to Compare</Label>
              <p className="text-sm text-gray-500 mt-1">3 structures configured</p>
            </div>
          </div>
          
          <Button 
            className="w-full" 
            onClick={runSimulation}
            disabled={simulating}
            data-testid="run-simulation-btn"
          >
            {simulating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Target className="w-4 h-4 mr-2" />
            )}
            Run What-If Simulation
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <>
          {/* Comparison Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Comparison Summary</span>
                <Badge variant="outline">{results.simulations.length} options analyzed</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Budget Range</p>
                  <p className="font-medium">
                    ${(results.comparison_summary.budget_range.min / 1000).toFixed(0)}K - 
                    ${(results.comparison_summary.budget_range.max / 1000).toFixed(0)}K
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Timeline Range</p>
                  <p className="font-medium">
                    {results.comparison_summary.timeline_range.min} - {results.comparison_summary.timeline_range.max} weeks
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Risk Range</p>
                  <p className="font-medium">
                    {results.comparison_summary.risk_range.min.toFixed(0)} - {results.comparison_summary.risk_range.max.toFixed(0)}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">ROI Range</p>
                  <p className="font-medium">
                    {results.comparison_summary.roi_range.min.toFixed(0)}% - {results.comparison_summary.roi_range.max.toFixed(0)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Simulation Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {results.simulations.map((result) => (
              <SimulationResultCard
                key={result.structure_id}
                result={result}
                isRecommended={result.structure_id === results.best_option}
                selected={result.structure_id === selectedStructure}
                onSelect={setSelectedStructure}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <p className="font-medium text-blue-800">Ready to proceed?</p>
              <p className="text-sm text-blue-600">Promote your selected option to Scenario Planning</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Sprint 24: Export PDF button */}
              <Button
                variant="outline"
                onClick={handleExportPDF}
                disabled={exporting}
                data-testid="export-comparison-pdf"
              >
                {exporting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowPromoteDialog(true)}
                disabled={!selectedStructure}
                data-testid="promote-to-scenario-btn"
              >
                Promote to Scenario
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Promote Dialog */}
      <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              Promote to Scenario
            </DialogTitle>
            <DialogDescription>
              This will create a new scenario in Scenario Planning based on your selected structure.
              No work items will be created until you explicitly promote the scenario.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="scenarioName">Scenario Name (optional)</Label>
              <Input
                id="scenarioName"
                placeholder={`What-If: ${demandTitle}`}
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
              />
            </div>
            
            {selectedStructure && results && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium">Selected Structure:</p>
                <p className="text-sm text-gray-600">
                  {results.simulations.find(s => s.structure_id === selectedStructure)?.structure_name}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePromote}
              disabled={promoting}
              data-testid="confirm-promote-btn"
            >
              {promoting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Create Scenario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatIfAnalysisPanel;
